import { ethers } from 'ethers'
import { useState, useEffect, type ReactElement } from 'react'
import { useRouter } from 'next/router'
import {
  Grid,
  Box,
  Tabs,
  Tab,
  styled,
  SvgIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  // Divider,
} from '@mui/material'
import css from './styles.module.css'
import { utils } from 'ethers'

import useDesktop from '@/hooks/useDesktop'
import { Body2White, Body1White } from '@/components/typography'
import PageCard from '@/components/common/PageCard'
import EthHashInfo from '@/components/common/EthHashInfo'
import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded'
import CallReceivedRoundedIcon from '@mui/icons-material/CallReceivedRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TokenIcon from '@/components/common/TokenIcon'
import InheritLogoIcon from '@/public/images/inheritwallet/logo-small-color.svg'
import useNativeTransferHistory from '@/hooks/useNativeTransferHistory'
import { useCurrentChain } from '@/hooks/useChains'
import useSystemHistory from '@/hooks/useSystemHistory'
// import useERC20TransferHistory from '@/hooks/useERC20TransferHistory'
import { formatDateTime } from '@/utils/date'
// import type { TokenTxItemProps } from '@/utils/routescan'
import type { SystemTxItemProps, TokenTxItemProps } from '@/utils/api'
// import { parseLogInputs } from '@/utils/log-parser'
import ContentPasteSearchRoundedIcon from '@mui/icons-material/ContentPasteSearchRounded'
import { camelCaseToSpaces, safeFormatUnits, formatVisualAmount, shortenText } from '@/utils/formatters'
import useSafeAddress from '@/hooks/useSafeAddress'
import { trackEvent, INHERIT_EVENTS } from '@/services/analytics'

// import InfiniteScroll from '@/components/common/InfiniteScroll'
// import SkeletonTxList from '@/components/common/PaginatedTxns/SkeletonTxList'
// import ErrorMessage from '@/components/tx/ErrorMessage'
// import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const StyledTab = styled(Tab)({
  color: 'var(--Ebony-Clay-700, #485581)',
  fontSize: '14px',
  fontWeight: 'bold',
  lineHeight: '160%',
  textTransform: 'none',
  '&.Mui-selected': {
    color: 'var(--Ebony-Clay-300, #D2D7E5)',
  },
})

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <Box
      className={css.txsContent}
      flex={1}
      alignItems="center"
      justifyContent="center"
      p={0}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </Box>
  )
}

// Update every 30 seconds
const REFRESH_DELAY = 30e3

const Activities = (): ReactElement => {
  const isDesktop = useDesktop()
  const router = useRouter()
  const [nativeCounter, setNativeCounter] = useState<number>(1)
  const [systemCounter, setSystemCounter] = useState<number>(0)
  // const [erc20Counter, setErc20Counter] = useState<number>(0)

  const nativeTranfer = useNativeTransferHistory(nativeCounter)
  const systemHistory = useSystemHistory(systemCounter)
  // const erc20History = useERC20TransferHistory(erc20Counter)

  const [tabId, setTabId] = useState(0)
  const chainInfo = useCurrentChain()

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1 && systemCounter === 0) {
      setSystemCounter(1)
    }
    trackEvent({ ...INHERIT_EVENTS.CHANGE_HISTORY_TAB, ...{ label: newValue === 0 ? 'Token' : 'System' } })
    setTabId(newValue)
  }

  useEffect(() => {
    if (nativeCounter === 0) return

    const timerId = setTimeout(() => setNativeCounter(nativeCounter + 1), REFRESH_DELAY)
    return () => clearTimeout(timerId)
  }, [nativeCounter])

  useEffect(() => {
    if (systemCounter === 0) return

    const timerId = setTimeout(() => setSystemCounter(systemCounter + 1), REFRESH_DELAY)
    return () => clearTimeout(timerId)
  }, [systemCounter])

  // useEffect(() => {
  //   if (erc20Counter === 0) return

  //   const timerId = setTimeout(() => setErc20Counter(erc20Counter + 1), REFRESH_DELAY)
  //   return () => clearTimeout(timerId)
  // }, [erc20Counter])

  return (
    <PageCard title="History">
      <Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabId}
            onChange={handleChangeTab}
            textColor="secondary"
            TabIndicatorProps={{
              style: {
                backgroundColor: '#5D5DFF',
              },
            }}
            // variant="scrollable"
            // scrollButtons="auto"
            // scrollButtons
            // allowScrollButtonsMobile
          >
            <StyledTab label="Token" {...a11yProps(0)} />
            {/* <StyledTab label="ERC20" {...a11yProps(1)} /> */}
            <StyledTab label="System" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={tabId} index={0}>
          <Box py="16px">
            {nativeTranfer.loading && (
              <LinearProgress
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'var(--Blue-Ribbon-600, #5D5DFF)',
                  },
                  marginY: '8px',
                }}
              />
            )}

            {!nativeTranfer.loading && (!nativeTranfer.page || nativeTranfer.page.length === 0) && <NoData />}
            {nativeTranfer.page?.map((group, index) => (
              <>
                <Body1White key={index} sx={{ marginBottom: '8px', color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                  {group.label}
                </Body1White>
                {group.items.map((item: TokenTxItemProps, index: number) => (
                  <TokenTxItem key={index} item={item} />
                ))}
              </>
            ))}
          </Box>
        </CustomTabPanel>
        {/* <CustomTabPanel value={tabId} index={1}>
          <Box py="16px">
            {erc20History.loading && (
              <LinearProgress
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'var(--Blue-Ribbon-600, #5D5DFF)',
                  },
                  marginY: '8px',
                }}
              />
            )}

            {!erc20History.loading && (!erc20History.page || erc20History.page.length === 0) && <NoData />}

            {erc20History.page?.map((group, index) => (
              <>
                <Body1White key={index} sx={{ marginBottom: '8px', color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                  {group.label}
                </Body1White>
                {group.items.map((item: TokenTxItemProps, index: number) => (
                  <TokenTxItem key={index} item={item} />
                ))}
              </>
            ))}
          </Box>
        </CustomTabPanel> */}
        <CustomTabPanel value={tabId} index={1}>
          <Box py="16px">
            {systemHistory.loading && (
              <LinearProgress
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'var(--Blue-Ribbon-600, #5D5DFF)',
                  },
                  marginY: '8px',
                }}
              />
            )}

            {!systemHistory.loading && (!systemHistory.page || systemHistory.page.length === 0) && <NoData />}

            {systemHistory.page?.map((group, index) => (
              <>
                <Body1White key={index} sx={{ marginBottom: '8px', color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                  {group.label}
                </Body1White>
                {group.items.map((item: SystemTxItemProps, index: number) => (
                  <InheritTxItem index={index} key={index} item={item} />
                ))}
              </>
            ))}

            {systemHistory.error && (
              <Body2White
                sx={{
                  color: 'var(--Supporting-Carnation, #FF2239)',
                  paddingX: '8px',
                }}
              >
                Error loading transactions
              </Body2White>
            )}
            {/* <SkeletonTxList /> */}
            {/* <Box textAlign="center">
              <InfiniteScroll onLoadMore={() => console.log('load more')} />
              <Divider />
            </Box> */}
          </Box>
        </CustomTabPanel>
      </Box>
    </PageCard>
  )
}

const NoData = () => {
  return (
    <Box mt="32px" textAlign="center">
      <ContentPasteSearchRoundedIcon sx={{ fontSize: '64px', color: 'var(--Ebony-Clay-700, #485581)' }} />
      <Body2White sx={{ color: 'var(--Ebony-Clay-600, #5B6B9C)', fontSize: '16px' }}>No transactions yet!</Body2White>
    </Box>
  )
}

const AmountWithIcon = ({ uiAmount, symbol, logoUri }: { uiAmount: string; symbol: string; logoUri: string }) => {
  return (
    <Grid container>
      <Grid item container xs="auto" alignContent="center">
        <Body1White sx={{ color: 'var(--Ebony-Clay-300, #AAB3CF)' }}>
          {uiAmount}{' '}
          <span style={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', fontWeight: 300, marginLeft: '4px' }}>
            {shortenText(symbol, 6)}
          </span>
        </Body1White>
      </Grid>
      <Grid item container xs ml="4px" alignContent="center">
        <TokenIcon logoUri={logoUri} size={16} tokenSymbol={symbol} />
      </Grid>
    </Grid>
  )
}

const TokenTxItem = ({ item }: { item: TokenTxItemProps }): ReactElement => {
  const isDesktop = useDesktop()
  return (
    <Accordion sx={{ border: '1px solid var(--Ebony-Clay-950, #2F354B)', marginBottom: '8px' }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
        sx={{
          background: 'none',
          // padding: '16px',
          paddingY: isDesktop ? '8px' : '8px',
          paddingX: isDesktop ? '8px' : '8px',
          paddingRight: isDesktop ? '8px' : '4px',
        }}
      >
        <Grid container>
          <Grid
            item
            container
            xs="auto"
            alignContent="center"
            justifyContent="flex-start"
            mr={isDesktop ? '16px' : '8px'}
          >
            {item.txType === 'Received' && (
              <CallReceivedRoundedIcon
                sx={{
                  background: 'var(--Ebony-Clay-950, #272A3E)',
                  borderRadius: '4px',
                  color: 'var(--Emerald-600, #22C55E)',
                  padding: '8px',
                  textAlign: 'center',
                  fontSize: '36px',
                }}
              />
            )}
            {item.txType === 'Sent' && (
              <CallMadeRoundedIcon
                sx={{
                  background: 'var(--Ebony-Clay-950, #272A3E)',
                  borderRadius: '4px',
                  color: 'var(--Cinnabar-600, #E54949)',
                  padding: '8px',
                  textAlign: 'center',
                  fontSize: '36px',
                }}
              />
            )}
          </Grid>
          <Grid item container xs>
            <Grid item container xs={12} sm={4} spacing={2}>
              <Grid item container xs="auto" alignContent="center">
                <Body1White sx={{ color: 'var(--Ebony-Clay-300, #AAB3CF)', width: isDesktop ? '80px' : 'auto' }}>
                  {item.txType}
                </Body1White>
              </Grid>
              <Grid item container xs="auto" alignContent="center">
                <AmountWithIcon
                  uiAmount={formatVisualAmount(item.amount, item.tokenInfo.decimals, isDesktop ? 6 : 6)}
                  symbol={item.tokenInfo.symbol}
                  logoUri={item.tokenInfo.logoUri}
                />
              </Grid>
              {/* {!isDesktop && (
                <Grid item container xs alignContent="center">
                  <CheckIcon fontSize="small" sx={{ color: 'var(--Emerald-600, #22C55E)' }} />
                </Grid>
              )} */}
            </Grid>
            <Grid item container xs={12} sm spacing={2}>
              <Grid item container xs="auto" alignContent="center">
                <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', width: isDesktop ? '40px' : 'auto' }}>
                  {item.txType === 'Sent' ? 'To' : 'From'}:
                </Body2White>
              </Grid>
              <Grid item container xs alignContent="center">
                <EthHashInfo
                  address={item.txType === 'Sent' ? item.to : item.from}
                  showName={false}
                  shortAddress={false}
                  showAvatar={false}
                  shortenLen={6}
                  showPrefix={false}
                  copyAddress={true}
                  hasExplorer={true}
                  color={'var(--Ebony-Clay-400, #AAB3CF)'}
                  fontWeight={'500'}
                />
              </Grid>
            </Grid>
            {isDesktop && (
              <Grid item container alignContent="center" justifyContent="center" xs={12} sm={2}>
                <Body1White sx={{ color: 'var(--Emerald-600, #22C55E)' }}>Confirmed</Body1White>
                {/* <CheckIcon sx={{ color: 'var(--Emerald-600, #22C55E)' }} /> */}
              </Grid>
            )}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          borderTop: '1px solid var(--Ebony-Clay-950, #2F354B)',
        }}
      >
        <Grid container spacing={2}>
          <Grid item container xs="auto" alignContent="center">
            <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', width: '60px' }}>Tx hash:</Body2White>
          </Grid>
          <Grid item container xs alignContent="center">
            <EthHashInfo
              address={item.txHash}
              showName={false}
              shortAddress={false}
              showAvatar={false}
              shortenLen={6}
              showPrefix={false}
              copyAddress={true}
              hasExplorer={true}
              color={'var(--Ebony-Clay-400, #AAB3CF)'}
              fontWeight={'300'}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item container xs="auto" alignContent="center">
            <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', width: '60px' }}>Time:</Body2White>
          </Grid>
          <Grid item container xs alignContent="center">
            <Body2White sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
              {formatDateTime(item.timestamp * 1000)}
            </Body2White>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

const InheritTxItem = ({ item, index }: { item: SystemTxItemProps; index: number }): ReactElement => {
  const isDesktop = useDesktop()
  const safeAddress = useSafeAddress()

  const renderAction = (action: string) => {
    if (action === 'Inherited' && item.wallet === safeAddress) return 'Distributed Inheritance'
    if (action === 'Inherited' && item.wallet !== safeAddress) return 'Claimed Inheritance'
    return camelCaseToSpaces(action)
  }

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
        sx={{
          background: 'none',
          padding: '16px',
          paddingY: isDesktop ? '8px' : '8px',
          paddingX: isDesktop ? '8px' : '8px',
        }}
      >
        <Grid container>
          <Grid
            item
            container
            xs="auto"
            alignContent="center"
            justifyContent="flex-start"
            mr={isDesktop ? '16px' : '8px'}
          >
            <Box
              sx={{
                background: 'var(--Ebony-Clay-950, #272A3E)',
                borderRadius: '4px',
                color: 'var(--Emerald-600, #22C55E)',
                padding: '8px',
                textAlign: 'center',
                display: 'flex',
              }}
            >
              <SvgIcon component={InheritLogoIcon} inheritViewBox />
            </Box>
          </Grid>
          <Grid item container xs>
            <Grid item container xs={12} sm={3} alignContent="center">
              <Body1White sx={{ color: 'var(--Ebony-Clay-300, #AAB3CF)' }}>{renderAction(item.action)}</Body1White>
            </Grid>
            <Grid item container xs={12} sm spacing={2}>
              <Grid item container xs="auto" alignContent="center">
                <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>Wallet:</Body2White>
              </Grid>
              <Grid item container xs alignContent="center">
                <EthHashInfo
                  address={item.wallet}
                  showName={false}
                  shortAddress={false}
                  showAvatar={false}
                  shortenLen={6}
                  showPrefix={false}
                  copyAddress={true}
                  hasExplorer={true}
                  color={'var(--Ebony-Clay-400, #AAB3CF)'}
                  fontWeight={'300'}
                />
              </Grid>
            </Grid>
            {isDesktop && (
              <Grid item container alignContent="center" justifyContent="center" xs={12} sm={2}>
                <Body1White sx={{ color: 'var(--Emerald-600, #22C55E)' }}>Confirmed</Body1White>
              </Grid>
            )}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          borderTop: '1px solid var(--Ebony-Clay-950, #2F354B)',
        }}
      >
        <Grid container spacing={2}>
          <Grid item container xs="auto" alignContent="center">
            <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', width: '110px' }}>Tx hash:</Body2White>
          </Grid>
          <Grid item container xs alignContent="center">
            <EthHashInfo
              address={item.txHash}
              showName={false}
              shortAddress={false}
              showAvatar={false}
              shortenLen={6}
              showPrefix={false}
              copyAddress={true}
              hasExplorer={true}
              color={'var(--Ebony-Clay-400, #AAB3CF)'}
              fontWeight={'300'}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item container xs="auto" alignContent="center">
            <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', width: '110px' }}>Time:</Body2White>
          </Grid>
          <Grid item container xs alignContent="center">
            <Body2White sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
              {formatDateTime(item.timestamp * 1000)}
            </Body2White>
          </Grid>
        </Grid>
        <SystemDataDetail item={item} />
      </AccordionDetails>
    </Accordion>
  )
}

const SystemDataDetail = ({ item }: { item: SystemTxItemProps }): ReactElement => {
  const { data, action } = item

  const notShowKeys = ['tokenLogo', 'isVerified', 'Token decimals', 'Token symbol']
  const notShowValues = ['--', ethers.constants.AddressZero]

  const renderValue = ({ key, value }: { key: string; value: string }) => {
    if (key.indexOf('amount') !== -1) {
      const decimals = data.find((item) => item.key === 'Token decimals')?.value || '0'
      const symbol = data.find((item) => item.key === 'Token symbol')?.value || ''
      const tokenLogo = data.find((item) => item.key === 'tokenLogo')?.value || ''
      return (
        <AmountWithIcon uiAmount={safeFormatUnits(value, parseInt(decimals))} symbol={symbol} logoUri={tokenLogo} />
      )
    }

    if (utils.isAddress(value)) {
      return (
        <EthHashInfo
          address={value}
          showName={false}
          shortAddress={false}
          showAvatar={false}
          shortenLen={6}
          showPrefix={false}
          copyAddress={true}
          hasExplorer={true}
          color={'var(--Ebony-Clay-400, #AAB3CF)'}
          fontWeight={'300'}
        />
      )
    }

    return value
  }

  return (
    <>
      {data.map((parsed: { key: string; value: string }, index: number) => {
        if (notShowKeys.includes(parsed.key)) return <></>
        if (notShowValues.includes(parsed.value)) return <></>

        return (
          <Box
            key={`more-data-${index}`}
            sx={
              index === 0
                ? { borderTop: '1px solid var(--Ebony-Clay-950, #2F354B)', marginTop: '8px', paddingTop: '8px' }
                : {}
            }
          >
            <Grid container spacing={2}>
              <Grid item container xs="auto" alignContent="center">
                <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', width: '110px' }}>{parsed.key}:</Body2White>
              </Grid>
              <Grid item container xs alignContent="center">
                <Body2White sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>{renderValue(parsed)}</Body2White>
              </Grid>
            </Grid>
          </Box>
        )
      })}
    </>
  )
}

export default Activities
